/**
 * VRG-545 — what a lead would be worth per year if they bought what they said they wanted.
 *
 * ## The model, in the founder's terms
 *
 *     monthly activations = units ÷ 12
 *     activation cost     = the GRADUATED per-activation rate applied to that
 *                           monthly volume, × 12
 *     plan cost           = the monthly price of `planInterest`, × 12
 *     potential revenue   = activation cost + plan cost
 *
 * ## ⚠️ THIS IS NOT A FORECAST, AND THE CELL MUST NOT PRINT ONE
 *
 * Both inputs are things a stranger typed on a marketing page: a units figure and a plan
 * they clicked. Neither is a commitment, and the figure is a *list-price arithmetic* on two
 * self-reported numbers — the same caveat every other ROI column on this grid carries.
 *
 * The sharper danger is narrower and is why this module returns `null` so readily.
 * vera-website SEEDS the units slider at 25,000. A row where the visitor never touched it
 * still arrives carrying 25,000, and pricing that produces a large, specific, entirely
 * fabricated number printed beside a real person's name under a heading reading "potential
 * revenue". Nobody reading the column could tell it was ours. So:
 *
 *   **if the units figure is not demonstrably the visitor's, there is no cell.**
 *
 * An empty cell is a fact ("we do not have their volume"). A default-derived figure is a
 * claim nobody made. See `websiteDefaultState` for the two strengths of that signal and why
 * the `units` case can only ever reach the weaker one.
 *
 * ## The ladder is READ, never written here
 *
 * `activation_tiers` rides on each plan row, synced out of the Stripe metered price
 * (`packages/types/src/billing/activation-tier.ts`). There is no hardcoded schedule in this
 * repo to fall back on and there must not be one: `hasActivationTiers` exists precisely so
 * a caller renders NOTHING rather than substitute a default, because a wrong price is worse
 * than no price. This module honours that — an unresolvable schedule returns `null`.
 *
 * As of writing, all eight priced plan rows carry the identical eight-band schedule
 * ($25 ≤100 · $20 ≤500 · $16 ≤1,000 · $12 ≤5,000 · $9 ≤10,000 · $7 ≤50,000 · $6 ≤100,000 ·
 * $5 thereafter), which is why a lead with no stated plan can still be priced — see
 * `activationSchedule`.
 */
import { potentialRevenue as sharedPotentialRevenue } from '@repo/types'

import type { SubscriptionPlan } from 'services/subscriptionApi'
import type { SignupEntry } from 'hooks/api/useSignups'

import { WEBSITE_FIELDS } from './websiteFields'
import { isVisitorSupplied } from './websiteValues'

/** The manifest's own `units` entry — the volume the whole model is built on. */
const UNITS_FIELD = WEBSITE_FIELDS.find((f) => f.id === 'units')

/*
  VRG-691 — THE MODEL MOVED to `@repo/types` (`gtm/sales-pipeline.ts#potentialRevenue`), because
  the Sales pipeline's ROI-basis deal value is the same figure and the API computes it. Two
  copies would drift on the first edit, and the guard `sales-pipeline.guard.spec.ts` fails if
  this file starts pricing on its own again. What stays here is the grid's half: deciding
  whether `units` is the visitor's (`isVisitorSupplied`), which needs the website manifest.

  ⚠️ `enterprise` IS PRICED AS SCALE (founder's decision 2026-09-07) — now
  `UNPRICED_TIER_SUBSTITUTES` in the shared module. It is an ASSUMPTION, flagged on the row.
*/

export interface PotentialRevenue {
  /** Annualised graduated activation spend, in the plans' currency (USD). */
  activationAnnual: number
  /** Annualised subscription price of the plan the visitor said they wanted. */
  planAnnual: number
  /** The two, summed. */
  total: number
  /** Monthly activations the ladder was priced on — `units / 12`, unrounded. */
  monthlyActivations: number
  /** The plan the price came from, or null when the visitor named none. */
  plan: SubscriptionPlan | null
  /** True when `plan` is a SUBSTITUTE for an unpriced tier the visitor actually picked. */
  planSubstituted: boolean
  /** The tier key the visitor picked, verbatim — including an unpriced one. */
  requestedTierKey: string | null
}

/**
 * Price a signup row. `null` whenever any input is ours rather than theirs, or missing.
 *
 * The `null` cases, each a deliberate refusal rather than an omission:
 *   - no ROI submission, or no `units` on it — nothing to price;
 *   - `units` is (or may be) the site's own seeded 25,000 — see this module's header;
 *   - a non-finite or non-positive `units`;
 *   - no usable activation schedule — a wrong price is worse than no price.
 */
export const potentialSubscriptionRevenue = (
  row: SignupEntry,
  plans: SubscriptionPlan[],
): PotentialRevenue | null => {
  const roi = row.roi_submission
  if (!roi || !UNITS_FIELD) return null
  /*
    ⚠️ THE GATE. `default` means the submission RECORDS that the units control was never
    moved; `default-inferred` means it does not record it and the figure equals our seeded
    25,000. Both are refused. A brand that genuinely makes 25,000 units a year is
    indistinguishable from one that left the slider alone, so this hides some real leads'
    figures — the right way round: a missing number is a fact, a fabricated one is not.
  */
  const unitsSupplied = isVisitorSupplied(row, UNITS_FIELD)
  // `amount` is Stripe `unit_amount`-shaped — ALWAYS CENTS — while an `activation_tiers` rate
  // is in DOLLARS; the shared model divides the plan half by 100.
  return sharedPotentialRevenue(
    { units: roi.units, unitsSupplied, planInterest: row.waitlist_submission?.planInterest },
    plans,
  ) as PotentialRevenue | null
}
